import { Flexbox, Text, borderRadius } from '@morressier/ts';
import { standaloneFetcher, VIDEO_API_URL } from 'api/livestream-rtmp';
import HeartLoadingSpinner from 'components/HeartLoadingSpinner';
import { FC } from 'react';
import { PusherHandler } from 'store/modules/pusher';
import styled from 'styled-components';
import useSwr, { mutate } from 'swr';
import { logger } from 'utils/logger';
import { RTMP_CHINA_STATUS_START, RTMP_LIVE_STREAM_STATUS_DISABLED } from 'utils/pusher';
import { CountDown } from '../Countdown';
import { useSessionTime } from '../hooks';
import { VideoPlayerRTMP } from '../NewVideoPlayer';

const VideoWrap = styled.div`
  ${borderRadius('small')};
  background: rgb(31, 34, 44);
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 5rem;
`;
// NOTE: Entrypoint could be changed for now we only have default entrypoint which is: live
const ENTRY_POINT = 'live';
const AGORA_PLAY_URL = process.env.NEXT_PUBLIC_AGORA_PLAY_URL || 'Missing server URL';

// TODO: Move all rtmpMux to its own Agora or provider collection.

const RTMPChina: FC<{ session: MorressierSession; userId: string }> = props => {
  const { session, userId } = props;
  const { isPreSession } = useSessionTime({
    startDate: session.start_date,
    endDate: session.end_date,
    options: { runPastDates: true },
  });
  const rtmpChinaStatusStart = RTMP_CHINA_STATUS_START(session._id);
  const rtmpStatusDisabled = RTMP_LIVE_STREAM_STATUS_DISABLED(session._id);
  const {
    data: rtmpStream,
    error: rtmpStreamError,
    mutate: boundMutate,
  } = useSwr<RtmpStream, Error>(`${VIDEO_API_URL}/${session._id}`, standaloneFetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  // NOTE: Agora does not send #EXT-X-ENDLIST once HLS live stream finish
  const handleEnded = e => {
    console.log('ENDED', e);
    if (e.type === 'ended') {
      if (rtmpStream) {
        boundMutate({ ...rtmpStream, mux_stream_status: 'idle' }, false);
      }
    }
  };

  //   const handleVideoPlayerError = (mediaError: MediaError) => {
  //     logger.log('MEDIA ERROR', mediaError);
  //     // if (mediaError && mediaError.code === 4) {
  //     //   if (rtmpStream) {
  //     //     boundMutate({ ...rtmpStream, mux_stream_status: 'idle' }, false);
  //     //   }
  //     // }
  //   };

  //   const handleRetryplaylist = (e: { playerTimeout: boolean; player }) => {
  //     console.log('RETRY', e);
  //     if (e.playerTimeout) {
  //       if (rtmpStream) {
  //         boundMutate({ ...rtmpStream, mux_stream_status: 'idle' }, false);
  //         e.player.src({});
  //       }
  //     }
  //   };

  if (rtmpStreamError) {
    return (
      <VideoWrap>
        <Flexbox justifyContent="center" alignItems="center">
          <Text fontWeight="semiBold" as="span" color="white">
            {rtmpStreamError.message}
          </Text>
        </Flexbox>
      </VideoWrap>
    );
  }

  if (!rtmpStream)
    return (
      <VideoWrap>
        <Flexbox justifyContent="center" alignItems="center">
          <HeartLoadingSpinner />
        </Flexbox>
      </VideoWrap>
    );

  const renderStream = () => {
    switch (rtmpStream.mux_stream_status) {
      case 'idle': {
        if (rtmpStream.stream_started_at) {
          return (
            <VideoWrap>
              <Flexbox justifyContent="center" alignItems="center">
                <Text size="h4_new" fontWeight="bold" as="span" color="white">
                  Thank you for watching
                </Text>
              </Flexbox>
            </VideoWrap>
          );
        }
        if (isPreSession) {
          return <CountDown session={session} />;
        }
        return (
          <VideoWrap>
            <Flexbox justifyContent="center" alignItems="center">
              <Text fontWeight="semiBold" as="span" color="white">
                Session is starting soon...
              </Text>
            </Flexbox>
          </VideoWrap>
        );
      }
      case 'active': {
        return (
          <VideoPlayerRTMP
            src={`${AGORA_PLAY_URL}/${ENTRY_POINT}/${session._id}/playlist.m3u8`}
            sessionId={session._id}
            options={{ autoplay: true }}
            eventHandlers={{
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              ended: handleEnded,
              //   errorHandler: handleVideoPlayerError,
              //   retryplaylist: handleRetryplaylist,
            }}
            muxMetaData={{
              viewer_user_id: userId,
              video_id: session._id,
              video_title: session.name,
              player_name: 'China - RTMP player',
            }}
          />
        );
      }
      case 'disabled': {
        return (
          <VideoWrap>
            <Flexbox justifyContent="center" alignItems="center">
              <Text size="h4_new" fontWeight="bold" as="span" color="white">
                This session has concluded
              </Text>
            </Flexbox>
          </VideoWrap>
        );
      }
      default: {
        throw new Error('RTMP status not implemented');
      }
    }
  };

  return (
    <>
      {renderStream()}
      <PusherHandler
        channel={rtmpChinaStatusStart.channelName}
        event={rtmpChinaStatusStart.eventType}
        onUpdate={() => {
          boundMutate(
            {
              ...rtmpStream,
              mux_stream_status: 'active',
              stream_started_at: new Date().toString(),
            },
            false,
          );
          mutate(
            `${VIDEO_API_URL}/${session._id}?isOrganizer=true`,
            {
              ...rtmpStream,
              mux_stream_status: 'active',
              stream_started_at: new Date().toString(),
            },
            false,
          );
        }}
      />
      <PusherHandler
        channel={rtmpStatusDisabled.channelName}
        event={rtmpStatusDisabled.eventType}
        onUpdate={() => {
          boundMutate({ ...rtmpStream, mux_stream_status: 'disabled' }, false);
          mutate(
            `${VIDEO_API_URL}/${session._id}?isOrganizer=true`,
            {
              ...rtmpStream,
              mux_stream_status: 'disabled',
              stream_ended_at: new Date().toString(),
            },
            false,
          );
        }}
      />
      {/* <PusherHandler
        channel={rtmpStatusStart.channelName}
        event={rtmpStatusStart.eventType}
        onUpdate={() => {
          boundMutate(
            {
              ...rtmpStream,
              mux_stream_status: 'active',
              stream_started_at: new Date().toString(),
            },
            false,
          );
          mutate(
            `${VIDEO_API_URL}/${session._id}?isOrganizer=true`,
            {
              ...rtmpStream,
              mux_stream_status: 'active',
              stream_started_at: new Date().toString(),
            },
            false,
          );
        }}
      /> */}
    </>
  );
};

export default RTMPChina;
